import { useMSSByAccountBySymbol } from '@src/providers/MarketSecurityStatusProvider';
import { ConnectionType, useConnectionStatusContext, useSecuritiesContext, useSyncedRef } from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useTradingSettings } from '../../../providers/TradingSettingsContext';
import {
  getMarketAccountActiveCondition,
  type GetMarketAccountActiveConditionParams,
} from '../conditions/getMarketAccountActiveCondition';
import {
  getMarketConfiguredCondition,
  type GetMarketConfiguredConditionParams,
} from '../conditions/useMarketConfiguredCondition';
import {
  getMSSCapabilityEnabledCondition,
  type GetMSSCapabilityEnabledConditionParams,
} from '../conditions/useMSSCapabilityEnabledCondition';
import { getMSSEnabledCondition, type GetMSSEnabledConditionParams } from '../conditions/useMSSEnabledCondition';
import type { AvailabilityCondition } from '../types';

/** A hook wrapper for getOrderMarketSelectorConditions */
export const useOrderMarketSelectorConditions = ({
  symbol,
  ulTokensForSymbol,
}: Pick<GetOrderMarketSelectorConditionsParams, 'symbol' | 'ulTokensForSymbol'>) => {
  const { useNewMarketSelector } = useTradingSettings();
  const { enableNewMarketSelectorMSSValidation } = useFeatureFlag();
  const enableMSSValidation = useNewMarketSelector && enableNewMarketSelectorMSSValidation;
  const { securitiesBySymbol } = useSecuritiesContext();

  const symbols = useMemo(
    () => compact(ulTokensForSymbol ? [symbol, ...ulTokensForSymbol.map(token => token.Symbol)] : [symbol]),
    [ulTokensForSymbol, symbol]
  );
  const mssByAccountBySymbol = useMSSByAccountBySymbol({ symbols, tag: `MarketSelector: Orders MSS Condition` });

  const { marketCredentialsWithConnectionStatusByMarketAccountName } = useConnectionStatusContext();
  // This stream is just used as reference data to disambiguate, and can be pretty noisy, so we make it stable to keep the output of this hook stable
  const marketCredentialsWithConnectionStatusByMarketAccountNameRef = useSyncedRef(
    marketCredentialsWithConnectionStatusByMarketAccountName
  );

  const conditions: AvailabilityCondition[] = useMemo(
    () =>
      getOrderMarketSelectorConditions({
        enableMSSValidation,
        symbol,
        mssByAccountBySymbol,
        securitiesBySymbol,
        ulTokensForSymbol,
        marketCredentialsWithConnectionStatusByMarketAccountName:
          marketCredentialsWithConnectionStatusByMarketAccountNameRef.current,
      }),
    [
      enableMSSValidation,
      symbol,
      mssByAccountBySymbol,
      securitiesBySymbol,
      ulTokensForSymbol,
      marketCredentialsWithConnectionStatusByMarketAccountNameRef,
    ]
  );

  return { conditions, allConditionsReady: conditions.every(c => c.ready) };
};

export type GetOrderMarketSelectorConditionsParams = {
  /** Whether or not to perform MSS validation. Temporary - to be removed in 2.52.0 (upon which it will always be true) */
  enableMSSValidation: boolean;
} & Omit<GetMarketAccountActiveConditionParams, 'disableIfTradingDisabled'> &
  Omit<GetMarketConfiguredConditionParams, 'connectionType'> &
  Omit<GetMSSCapabilityEnabledConditionParams & GetMSSEnabledConditionParams, 'capability' | 'connectionType'>;

/**
 *  Given the necessary parameters and reference data, will build and return an array of AvailabilityConditions to be used
 * when building MarketSelectorItems for the Orders use case.
 */
export function getOrderMarketSelectorConditions({
  enableMSSValidation,
  symbol,
  ulTokensForSymbol,
  marketCredentialsWithConnectionStatusByMarketAccountName,
  mssByAccountBySymbol,
  securitiesBySymbol,
}: GetOrderMarketSelectorConditionsParams): AvailabilityCondition[] {
  const marketConfiguredCondition = getMarketConfiguredCondition({ connectionType: ConnectionType.Orders });
  const marketAccountActiveCondition = getMarketAccountActiveCondition({
    disableIfTradingDisabled: true,
    marketCredentialsWithConnectionStatusByMarketAccountName,
  });
  const mssMarketDataEnabledCondition = getMSSCapabilityEnabledCondition({
    connectionType: ConnectionType.Orders,
    capability: 'MarketData',
    mssByAccountBySymbol,
    symbol,
    securitiesBySymbol,
    ulTokensForSymbol,
  });
  const mssEnabledCondition = getMSSEnabledCondition({
    connectionType: ConnectionType.Orders,
    mssByAccountBySymbol,
    symbol,
    securitiesBySymbol,
    ulTokensForSymbol,
  });

  return compact([
    marketConfiguredCondition,
    marketAccountActiveCondition,
    // We temporarily disable these conditionaly. Conditionality will be removed in 2.52.0
    enableMSSValidation && mssMarketDataEnabledCondition,
    enableMSSValidation && mssEnabledCondition,
  ]);
}
